<div class="admin-import-module">
  <app-top-header
    [parent]="parent"
    backRouterLink="/admin"
    backRouterLinkLabel="TRANS__SECTION__ADMIN__TITLE"
    currentLabel="TRANS__IMPORT__HEADING"
    class="admin-header-container"
  ></app-top-header>

  <!-- The step bar is the page's header, on every step: the page wrapper
  has no heading or description, and each step's own title and intro sit
  under the bar. -->
  <app-header-and-description>
    <div #stepBarWrapper class="admin-import-module__bar">
      <app-import-step-bar
        id="import-module-steps"
        [view]="view"
        [mappingOpen]="mappingStepOpen"
        [laterOpen]="laterStepsOpen"
        [sourceTable]="store.selectedTable"
        [objectTypeName]="store.selectedObjectType?.name ?? null"
        [mappingLines]="store.lineCount"
        [usedColumns]="store.usedColumns.size"
        [columnCount]="store.columns.length"
        [undecidedValues]="undecidedValues"
        [warnedRows]="store.preflightResult?.summary.rows_with_warnings ?? null"
        [runFinished]="!!store.runJob.runResult"
        [profileName]="store.selectedProfileName"
        [profiles]="store.profiles"
        [profileId]="store.selectedProfileId"
        [unsaved]="store.profileUnsaved"
        [canSaveAsNew]="store.hasMappings"
        [profileLoaded]="!!store.selectedProfileId"
        [busy]="store.savingProfile || !!store.deletingProfileId || store.applyingProfile"
        (viewChange)="goToView($event)"
        (save)="saveProfileFromStepBar()"
        (saveAsNew)="startSaveAsNew()"
        (deleteProfile)="deleteLoadedProfile()"
        (pickProfile)="store.pickProfile($event)"
        (exportProfile)="store.exportProfile()"
        (importProfile)="store.importProfileFile($event)"
      ></app-import-step-bar>
      @if (savingAsNew) {
        <div
          class="admin-import-module__save-as-new"
          role="dialog"
          [attr.aria-label]="'TRANS__IMPORT__PROFILE__SAVE_AS_NEW' | translate"
          (keydown.escape)="cancelSaveAsNew()"
        >
          <div class="admin-import-module__save-profile">
            <mat-form-field appearance="outline" subscriptSizing="dynamic">
              <mat-label>{{ 'TRANS__IMPORT__PROFILE__NAME' | translate }}</mat-label>
              <input #saveAsNewName matInput [formControl]="saveProfileNameControl" [matAutocomplete]="saveProfileNames">
              <mat-autocomplete #saveProfileNames="matAutocomplete">
                @for (name of saveProfileNameSuggestions; track name) {
                  <mat-option [value]="name">{{ name }}</mat-option>
                }
              </mat-autocomplete>
            </mat-form-field>
            <button
              mat-flat-button
              type="button"
              color="primary"
              [disabled]="store.savingProfile || !saveProfileNameControl.value.trim() || !!store.mediaPatternProblem"
              (click)="saveProfile()"
            >
              <span class="bold uppercase">{{ 'TRANS__IMPORT__PROFILE__SAVE' | translate }}</span>
            </button>
            <button mat-button type="button" [disabled]="store.savingProfile" (click)="cancelSaveAsNew()">
              {{ 'TRANS__IMPORT__VALUE_REVIEW__CANCEL' | translate }}
            </button>
          </div>
          @if (saveProfileNameCollides) {
            <p class="admin-import-module__warn">{{ 'TRANS__IMPORT__PROFILE__NAME_COLLIDES' | translate }}</p>
          }
          @if (store.saveProfileStatus) {
            <p class="admin-import-module__save-status">{{ store.saveProfileStatus }}</p>
          }
        </div>
      }
    </div>
    @if (store.profileApplyError) {
      <p class="admin-import-module__profile-error">{{ store.profileApplyError }}</p>
    }
    @let heading = stepHeading;
    <div class="admin-import-module__step-head">
      <h3 class="admin-import-module__step-title">{{ heading.titleKey | translate }}</h3>
      <p class="admin-import-module__step-intro">
        @for (key of heading.introKeys; track $index) {
          {{ key | translate }}
        }
      </p>
    </div>
    @if (onMappingStep) {
      <nav
        mat-tab-nav-bar
        mat-stretch-tabs="false"
        mat-align-tabs="start"
        class="admin-import-module__tabs"
        [tabPanel]="mappingTabPanel"
      >
        @for (tab of mappingTabs; track tab.view) {
          @let active = view === tab.view;
          <a mat-tab-link [active]="active" (click)="goToView(tab.view)">
            {{ tab.labelKey | translate }}
            <span class="admin-import-module__tab-count" [class.admin-import-module__tab-count--active]="active">
              {{ tabCount(tab.view) }}
            </span>
            @if (tab.view === 'kodeoppslag' && store.lookupMissedCodes; as missed) {
              <span class="admin-import-module__tab-note">
                {{ (missed === 1 ? 'TRANS__IMPORT__LOOKUPS__TAB_MISSES_ONE' : 'TRANS__IMPORT__LOOKUPS__TAB_MISSES') | translate: {count: missed} }}
              </span>
            }
            @if (tab.view === 'tomme-verdier' && store.blankValueSuggestions.length; as open) {
              <span class="admin-import-module__tab-note">
                {{ 'TRANS__IMPORT__BLANK_VALUES__TAB_SUGGESTIONS' | translate: {count: open} }}
              </span>
            }
          </a>
        }
      </nav>
    }
    @if (store.sourceError) {
      <p class="admin-import-module__warn bold">{{ store.sourceError }}</p>
    }
    <div class="admin-import-module__container">
      <div class="admin-import-module__step" [class.admin-import-module__hidden]="view !== 'kilde'">
        <app-import-source-step *appDetachWhile="view !== 'kilde'"></app-import-source-step>
      </div>

      <mat-tab-nav-panel #mappingTabPanel [class.admin-import-module__hidden]="!onMappingStep">
        <div class="admin-import-module__step" [class.admin-import-module__hidden]="view !== 'feltkoblinger'">
          <app-import-mapping-step
            *appDetachWhile="view !== 'feltkoblinger'"
          ></app-import-mapping-step>
        </div>

        <div class="admin-import-module__step" [class.admin-import-module__hidden]="view !== 'kodeoppslag'">
          <app-import-lookups-step *appDetachWhile="view !== 'kodeoppslag'"></app-import-lookups-step>
        </div>

        <div class="admin-import-module__step" [class.admin-import-module__hidden]="view !== 'tomme-verdier'">
          <app-import-blank-values-step *appDetachWhile="view !== 'tomme-verdier'"></app-import-blank-values-step>
        </div>
      </mat-tab-nav-panel>

      @if (showValueReview) {
        <div class="admin-import-module__step">
          <app-value-review-page
            [fields]="store.conceptFields"
            [loading]="store.loadingConceptValues"
            [rowsScanned]="store.conceptRowsScanned"
            [objectType]="store.selectedObjectType?.superobject_type_id ?? ''"
            [resolveLabel]="store.resolveConceptLabel"
            [resolveColumns]="store.resolveConceptColumns"
            (decisionsChanged)="store.conceptDecisionsChanged()"
            (rescan)="store.scanConceptValues()"
          ></app-value-review-page>
        </div>
      }

      <div class="admin-import-module__step" [class.admin-import-module__hidden]="view !== 'kontroll'">
        <app-import-run-step
          *appDetachWhile="view !== 'kontroll'"
          (openValueReview)="openValueReview()"
        ></app-import-run-step>
      </div>
    </div>
    <div class="admin-import-module__step-footer">
      @if (footerBack; as back) {
        <button
          mat-stroked-button
          type="button"
          color="primary"
          class="admin-import-module__step-back"
          (click)="goToView(back.view)"
        >
          <mat-icon>navigate_before</mat-icon>
          <span class="bold uppercase">{{ back.labelKey | translate }}</span>
        </button>
      }
      @if (footerHint; as hint) {
        <span class="admin-import-module__step-footer-hint" [id]="'import-module-' + hint.step + '-next-hint'">
          {{ hint.key | translate: hint.params }}
        </span>
      }
      @if (footerNext; as next) {
        <button
          mat-flat-button
          type="button"
          color="primary"
          class="admin-import-module__step-next"
          [disabled]="!isViewOpen(next.view)"
          (click)="goToView(next.view)"
        >
          <span class="bold uppercase">{{ next.labelKey | translate }}</span>
          <mat-icon iconPositionEnd>navigate_next</mat-icon>
        </button>
      }
    </div>
  </app-header-and-description>
</div>
